import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Card, CardContent } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Wrench, Search, Download, Wifi, WifiOff, Zap, PackagePlus } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { downloadCsv as exportCsv } from '@/shared/utils/download';
import { SAV_TYPE_META as TYPE_META } from '@/shared/constants/assetTypes';
import { TAILLE_PAGE, lireBornes } from '@/shared/api/troncature';
import BandeauTroncature from '@/shared/components/BandeauTroncature';
import BarrePagination from '@/shared/components/BarrePagination';
import FicheProduitDialog from '@/features/distributeur/components/FicheProduitDialog';
import ProduitsAjoutesSav from '@/features/distributeur/components/ProduitsAjoutesSav';

// Le Radiateur R2 ne se propose plus au filtre : « ça sert à rien » (call du 01/10).
// Retiré d'ICI seulement, par décision : le type reste connu ailleurs (parcours poseur,
// revenus Survoltage), et un actif R2 existant reste affiché avec son étiquette.
const TYPES_HORS_FILTRE = new Set(['Radiateur R2']);

// La route de la nouvelle entrée de menu « Ajouter un produit » : elle ouvre la fiche
// par-dessus cette page, et la fermer y revient.
export const CHEMIN_NOUVEAU_PRODUIT = '/sav/produits/nouveau';

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

export default function SAVPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ficheDepuisLeMenu = pathname === CHEMIN_NOUVEAU_PRODUIT;
  const [fiche, setFiche] = useState({ ouverte: false, produit: null });
  const [versionProduits, setVersionProduits] = useState(0);
  const [typesSousCompte, setTypesSousCompte] = useState([]);

  useEffect(() => {
    if (ficheDepuisLeMenu) setFiche({ ouverte: true, produit: null });
  }, [ficheDepuisLeMenu]);

  useEffect(() => {
    api.get('/distributeur/types-produits')
      .then(r => setTypesSousCompte(r.data?.types || []))
      .catch(() => {});
  }, [versionProduits]);

  const fermerFiche = () => {
    setFiche({ ouverte: false, produit: null });
    if (ficheDepuisLeMenu) navigate('/sav');
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statutFilter, setStatutFilter] = useState('all');
  const [installerFilter, setInstallerFilter] = useState('all');
  const [marqueFilter, setMarqueFilter] = useState('all');

  const [bornes, setBornes] = useState({ total: null, page: 1, taille: TAILLE_PAGE, tronque: false, borne: null });
  const [page, setPage] = useState(1);
  // Les listes deroulantes viennent de la BASE, pas des lignes chargees. Deduites de
  // la page, elles ne proposeraient que les marques de la page courante : le
  // depanneur qui cherche « toutes les Atlantic » ne les trouverait dans le menu que
  // si la page 1 en contient une.
  const [filtres, setFiltres] = useState({ marques: [], types: [], installateurs: [] });

  useEffect(() => {
    api.get('/distributeur/sav-filtres')
      .then(r => setFiltres(r.data || { marques: [], types: [], installateurs: [] }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, taille: TAILLE_PAGE };
    if (q.trim()) params.search = q.trim();
    if (typeFilter !== 'all') params.type = typeFilter;
    if (statutFilter !== 'all') params.statut_connexion = statutFilter;
    if (installerFilter !== 'all') params.installateur = installerFilter;
    if (marqueFilter !== 'all') params.marque = marqueFilter;
    api.get('/distributeur/sav-equipements', { params })
      .then(r => { setItems(r.data || []); setBornes(lireBornes(r)); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, q, typeFilter, statutFilter, installerFilter, marqueFilter]);

  useEffect(() => { setPage(1); },
    [q, typeFilter, statutFilter, installerFilter, marqueFilter]);

  // On route par NOTRE identifiant de chantier (« CH-4F2A9C »), pas par la référence
  // du poseur. Elle reste l'unité de travail du distributeur, mais elle est LIBRE :
  // deux chantiers peuvent la partager, et ouvrir par elle valait alors un refus 409 à
  // un dépanneur qui a le client au téléphone. L'identifiant, lui, en désigne un seul.
  //
  // La référence libre reste le repli pour les actifs écrits avant la reprise des
  // identifiants, et le client le repli du repli. Sa fiche est tout aussi restreinte —
  // aucun nom, aucune adresse, aucune donnée financière.
  const ouvrirChantier = (a) => {
    const cle = a.reference_unique || a.reference_chantier;
    if (cle) navigate(`/sav/chantier/${encodeURIComponent(cle)}`);
    else if (a.client_id) navigate(`/sav/client/${a.client_id}`);
  };

  // Les types du filtre : ceux que l'écran sait dessiner, moins le R2, plus ceux que le
  // sous-compte a créés (un type ajouté par le SAV doit pouvoir se filtrer).
  const optionsType = useMemo(() => {
    const connus = Object.entries(TYPE_META)
      .filter(([k]) => !TYPES_HORS_FILTRE.has(k))
      .map(([k, m]) => [k, m.label]);
    const crees = typesSousCompte
      .filter(t => !TYPE_META[t] && !TYPES_HORS_FILTRE.has(t))
      .map(t => [t, t]);
    return [...connus, ...crees];
  }, [typesSousCompte]);

  // Plus aucun filtrage ici : les cinq criteres partent au serveur, qui les resout
  // sur la totalite du perimetre et non sur la page. Filtrer une seconde fois ce que
  // le serveur a deja filtre serait au mieux inutile, au pire trompeur.
  const filtered = items;

  const downloadCsv = async () => {
    try {
      await exportCsv('/distributeur/sav-export.csv', `parc_sav_${new Date().toISOString().slice(0, 10)}.csv`);
    } catch { /* silent */ }
  };

  return (
    <div className="p-6 space-y-5" data-testid="sav-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-marque" /> SAV distributeur
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">Produits enregistrés par CFT Sign et Flexcore, et ceux que vous ajoutez. Cliquez une ligne pour ouvrir la fiche chantier, y corriger un numéro de série et consigner une interaction.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" data-testid="sav-count">{(bornes.total ?? filtered.length).toLocaleString('fr-FR')} équipement{(bornes.total ?? filtered.length) > 1 ? 's' : ''}</Badge>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="sav-export-csv"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
          <Button size="sm" onClick={() => setFiche({ ouverte: true, produit: null })} data-testid="sav-ajouter-produit"><PackagePlus className="w-3.5 h-3.5 mr-1" /> Ajouter un produit</Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                className="h-9 pl-8"
                placeholder="Rechercher par n° série, marque, modèle, commune, réf. chantier..."
                value={q}
                onChange={e => setQ(e.target.value)}
                data-testid="sav-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9" data-testid="sav-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {optionsType.map(([k, libelle]) => (
                  <SelectItem key={k} value={k}>{libelle}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="h-9" data-testid="sav-statut-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts connexion</SelectItem>
                <SelectItem value="connecte">Connecté</SelectItem>
                <SelectItem value="deconnecte">Déconnecté</SelectItem>
                <SelectItem value="en_attente_connexion">En attente</SelectItem>
                <SelectItem value="inconnu">Inconnu</SelectItem>
              </SelectContent>
            </Select>
            {filtres.installateurs.length > 1 && (
              <Select value={installerFilter} onValueChange={setInstallerFilter}>
                <SelectTrigger className="h-9" data-testid="sav-installer-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous installateurs</SelectItem>
                  {filtres.installateurs.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {filtres.marques.length > 0 && (
              <Select value={marqueFilter} onValueChange={setMarqueFilter}>
                <SelectTrigger className="h-9" data-testid="sav-marque-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes marques</SelectItem>
                  {filtres.marques.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>


      <BandeauTroncature etat={bornes} quoi="equipements" />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-sm text-neutral-400 py-12 text-center">Chargement...</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-neutral-400 py-12 text-center" data-testid="sav-empty">Aucun équipement</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-neutral-50 border-b border-neutral-100">
                  <tr className="text-left text-neutral-500 text-[11px] uppercase tracking-wide">
                    <th className="py-2.5 px-4 font-medium">Type</th>
                    <th className="py-2.5 px-4 font-medium">Marque / Modèle</th>
                    <th className="py-2.5 px-4 font-medium">N° série</th>
                    <th className="py-2.5 px-4 font-medium">Puissance / Production</th>
                    <th className="py-2.5 px-4 font-medium">Date pose</th>
                    <th className="py-2.5 px-4 font-medium">Réf. chantier</th>
                    <th className="py-2.5 px-4 font-medium">Statut</th>
                    <th className="py-2.5 px-4 font-medium">Commune</th>
                    <th className="py-2.5 px-4 font-medium">Installateur</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a, i) => {
                    const meta = TYPE_META[a.type] || TYPE_META.Autre;
                    const Icon = meta.Icon;
                    const connOk = a.statut_connexion === 'connecte';
                    return (
                      <tr key={a.id} onClick={() => ouvrirChantier(a)} className={`border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors cursor-pointer ${i % 2 ? 'bg-white' : 'bg-white'}`} data-testid={`sav-row-${a.id}`}>
                        <td className="py-3 px-4">
                          <div className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {a.type || '-'}
                          </div>
                          {/* Ce qui distingue deux équipements du même type : la
                              technologie décide du profil de flexibilité, le contrat
                              d'obligation d'achat décide de ce qu'on a le droit de
                              piloter. Le SAV a besoin des deux avant de conseiller. */}
                          {a.technologie && (
                            <div className="mt-1 text-[10px] text-neutral-500 capitalize" data-testid={`sav-technologie-${a.id}`}>
                              {a.technologie === 'r2' ? 'R2 (délestage)' : a.technologie}
                            </div>
                          )}
                          {a.contrat_oa && (
                            <span className="mt-1 inline-block rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700" data-testid={`sav-contrat-oa-${a.id}`}>
                              Contrat OA
                            </span>
                          )}
                          {(a.nombre_ballons || a.nombre_radiateurs) && (
                            <div className="mt-0.5 text-[10px] text-neutral-400" data-testid={`sav-nombre-${a.id}`}>
                              × {a.nombre_ballons || a.nombre_radiateurs}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-neutral-900">{a.marque || '-'}</div>
                          <div className="text-[11px] text-neutral-400">{a.modele || '-'}</div>
                        </td>
                        <td className="py-3 px-4 font-mono text-[12px] text-neutral-700">
                          {a.identifiant_equipement || '-'}
                          {/* Les numéros gravés sur le matériel annexe. Un PV en panne se
                              diagnostique par la centrale et la passerelle, pas par
                              l'identifiant de l'actif ; une PAC par son thermostat. */}
                          {a.type === 'PV' && a.onduleur_id && (
                            <div className="mt-0.5 font-sans text-[10px] text-neutral-400" data-testid={`sav-onduleur-centrale-${a.id}`}>
                              ID centrale : {a.onduleur_id}
                            </div>
                          )}
                          {a.type === 'PV' && a.onduleur_serial && (
                            <div className="mt-0.5 font-sans text-[10px] text-neutral-400" data-testid={`sav-onduleur-serie-${a.id}`}>
                              Passerelle S/N : {a.onduleur_serial}
                            </div>
                          )}
                          {a.type === 'PV' && a.onduleur_modele && (
                            <div className="mt-0.5 font-sans text-[10px] text-neutral-400" data-testid={`sav-onduleur-modele-${a.id}`}>
                              Modèle onduleur : {a.onduleur_modele}
                            </div>
                          )}
                          {a.type === 'PAC' && a.thermostat_serial && (
                            <div className="mt-0.5 font-sans text-[10px] text-neutral-400" data-testid={`sav-thermostat-serie-${a.id}`}>
                              Thermostat S/N : {a.thermostat_serial}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4" data-testid={`sav-puissance-${a.id}`}>
                          {a.puissance_kw ? (
                            <div className="flex items-center gap-1 text-[12px] font-medium text-neutral-800">
                              <Zap className="h-3 w-3 text-amber-500" />
                              {/* L'unité dépend du type : un PV se compte en kWc posés,
                                  une batterie en kWh stockés. Un « kW » partout laissait
                                  comparer des grandeurs qui ne se comparent pas. */}
                              {a.puissance_kw} {a.type === 'PV' ? 'kWc' : a.type === 'Batterie' ? 'kWh' : 'kW'}
                            </div>
                          ) : <span className="text-neutral-300">—</span>}
                          {a.production_annuelle_kwh && (
                            <div className="mt-0.5 text-[10.5px] font-medium text-emerald-600">
                              {Math.round(a.production_annuelle_kwh)} kWh/an
                            </div>
                          )}
                          {a.type === 'PV' && a.nombre_panneaux && (
                            <div className="mt-0.5 text-[10px] text-neutral-500">
                              {a.nombre_panneaux} × {a.puissance_panneau_wc || '?'} Wc
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-neutral-700">{fmtDate(a.date_installation)}</td>
                        <td className="py-3 px-4 text-neutral-700">{a.reference_chantier || '-'}</td>
                        <td className="py-3 px-4">
                          {connOk
                            ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><Wifi className="w-3 h-3" /> Connecté</span>
                            : <span className="inline-flex items-center gap-1 text-[11px] text-neutral-400"><WifiOff className="w-3 h-3" /> {a.statut_connexion || 'inconnu'}</span>}
                        </td>
                        <td className="py-3 px-4">
                          {/* La commune, jamais le nom du client (call du 01/10). */}
                          <div className="text-neutral-700">{[a.client_code_postal, a.client_ville].filter(Boolean).join(' ') || '-'}</div>
                        </td>
                        <td className="py-3 px-4 text-neutral-600">{a.installateur_nom || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BarrePagination etat={bornes} onPage={setPage} quoi="equipements" busy={loading} />

      <ProduitsAjoutesSav
        recherche={q.trim()} type={typeFilter} version={versionProduits}
        onModifier={(p) => setFiche({ ouverte: true, produit: p })}
      />

      <FicheProduitDialog
        ouvert={fiche.ouverte} produit={fiche.produit} types={typesSousCompte}
        onFerme={fermerFiche}
        onEnregistre={() => { setVersionProduits(v => v + 1); fermerFiche(); }}
      />
    </div>
  );
}
